import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, PaperPlaneTilt, Eye, PencilSimple, UploadSimple, FileText, Lightning, CheckCircle, Clock, LinkSimple, Certificate, EnvelopeSimple, EnvelopeOpen, ArrowsClockwise, TreeStructure, ArrowClockwise, Trash, X as XIcon, Hourglass, MapTrifold, Notebook, Files, Archive, Users, ArrowCounterClockwise } from "@phosphor-icons/react";
import { ScoreRadar, ScoreBars, IndicesBadges } from "@/components/ScoreCharts";
import OrgChart from "@/components/OrgChart";
import PageHeader from "@/components/PageHeader";
import EmployeeDetailDialog from "@/components/EmployeeDetailDialog";
import SchedaCard from "@/components/SchedaCard";
import EmployeeCertificationsTab from "@/components/EmployeeCertificationsTab";
import ConfirmDelete from "@/components/ConfirmDelete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEPARTMENTS = ["Vendite","Marketing","Amministrazione","Acquisti","Logistica","Magazzino","Ufficio Tecnico","Produzione","IT","HR","Legale","Finanza","Operazioni","Customer Service","Altro"];

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  // BUG-64/65: query params (?tab=schede&expand=<id>) + tab controlled
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "schede" ? "schede"
    : searchParams.get("tab") === "organigramma" ? "organigramma"
    : searchParams.get("tab") === "certificazioni" ? "certificazioni" : "lista";
  const [activeTab, setActiveTab] = useState(initialTab);
  const initialExpand = searchParams.get("expand") || null;
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewEmpId, setViewEmpId] = useState(null);
  const [deptFilter, setDeptFilter] = useState("");
  const [mgrFilter, setMgrFilter] = useState("");
  const [statusTab, setStatusTab] = useState("attivi"); // attivi|archivio|cestino
  const [archivedEmps, setArchivedEmps] = useState([]);
  const [byDeptData, setByDeptData] = useState({ groups: [], archived_count: 0, total_active: 0 });
  // C19: row click default action (card | modal). LocalStorage persistence.
  const [rowAction, setRowAction] = useState(() => {
    try { return localStorage.getItem("selly_employee_row_default_action") || "card"; } catch { return "card"; }
  });
  // MIG-25: archive dialog state
  const [archiveDlg, setArchiveDlg] = useState(null); // employee
  const [archiveForm, setArchiveForm] = useState({ reason: "dimissioni", last_working_day: "", notes: "" });
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ first_name: "", last_name: "", email: "", department: "", is_manager: false, send_email: false });
  const [inviteLink, setInviteLink] = useState("");
  // FIX-4 BUG-37: filtro stato registrazione + dialog conferma cancel
  const [regStatusFilter, setRegStatusFilter] = useState("all");
  // FEATURE-01: filtro colonna Piano Sviluppo
  const [planFilter, setPlanFilter] = useState("all"); // all|nessun_piano|da_approvare|da_fare|in_corso|completato
  const [planSort, setPlanSort] = useState("priority_asc"); // priority_asc | priority_desc | null (alphabetical)
  // FEATURE-04: filtro certificazioni
  const [certFilter, setCertFilter] = useState("all"); // all|expiring|expired
  const [certSummary, setCertSummary] = useState({}); // emp_id → {valida, in_scadenza, scaduta, items}
  const [expandedScheda, setExpandedScheda] = useState(initialExpand); // FEATURE-02 + BUG-64
  // BUG-64: sync state ↔ URL when tab/expand changes
  useEffect(() => {
    const params = {};
    if (activeTab !== "lista") params.tab = activeTab;
    if (expandedScheda) params.expand = expandedScheda;
    setSearchParams(params, { replace: true });
  }, [activeTab, expandedScheda, setSearchParams]);
  // BUG-64: scroll-into-view della scheda quando arriva da deep-link
  const didInitialScrollRef = useRef(false);
  useEffect(() => {
    if (didInitialScrollRef.current || !initialExpand || !employees.length) return;
    if (activeTab !== "schede") return;
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="scheda-${initialExpand}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        didInitialScrollRef.current = true;
      }
    }, 350);
  }, [activeTab, initialExpand, employees.length]);
  const [confirmCancelEid, setConfirmCancelEid] = useState(null);
  const [form, setForm] = useState({ first_name:"",last_name:"",email:"",phone:"",job_title:"",department:"",classification:"",ral:0,years_experience:0,is_manager:false,managed_count:0,sector:"",manager_id:"",hr_notes:"",mansionario_id:"" });
  const [mansionari, setMansionari] = useState([]);
  const [trashCount, setTrashCount] = useState(0);

  const { activeWorkspaceId } = useAuth();
  // FIX-4 BUG-37: include_pending=true per vedere i record placeholder in tabella Lista
  const load = () => axios.get(`${API}/employees?status=${statusTab}&include_pending=true`, { withCredentials: true })
    .then(r => setEmployees(r.data))
    .catch(() => {});
  // Mantieni count cestino sempre aggiornato per badge tab
  const loadTrashCount = () => axios.get(`${API}/employees?status=cestino`, { withCredentials: true })
    .then(r => setTrashCount((r.data || []).length))
    .catch(() => setTrashCount(0));
  // MIG-25: load archive list + by-department
  const loadArchive = () => axios.get(`${API}/employees?status=archivio`, { withCredentials: true })
    .then(r => setArchivedEmps(r.data || []))
    .catch(() => setArchivedEmps([]));
  const loadByDept = () => axios.get(`${API}/employees/by-department`, { withCredentials: true })
    .then(r => setByDeptData(r.data || { groups: [], archived_count: 0, total_active: 0 }))
    .catch(() => {});
  // Batch 62.A: re-load on workspace switch for agency accounts
  useEffect(() => { load(); }, [statusTab, activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadTrashCount(); loadArchive(); loadByDept(); }, [employees, activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps
  // B71 FIX-3: refetch on focus
  useRefetchOnFocus(load);
  // FEATURE-04: carica summary certificazioni workspace per filtro lista
  useEffect(() => {
    axios.get(`${API}/certifications-v2/summary`, { withCredentials: true })
      .then(r => setCertSummary(r.data || {}))
      .catch(() => setCertSummary({}));
  }, [activeWorkspaceId, employees.length]);
  useEffect(() => {
    if (!activeWorkspaceId) { setMansionari([]); return; }
    axios.get(`${API}/library/job-descriptions?workspace_id=${activeWorkspaceId}`, { withCredentials: true })
      .then(r => setMansionari(r.data || [])).catch(() => {});
  }, [activeWorkspaceId]);
  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const resetForm = () => { setForm({ first_name:"",last_name:"",email:"",phone:"",job_title:"",department:"",classification:"",ral:0,years_experience:0,is_manager:false,managed_count:0,sector:"",hr_notes:"",mansionario_id:"" }); setEditId(null); };

  const restoreEmp = async (eid) => {
    try {
      await axios.post(`${API}/employees/${eid}/restore`, {}, { withCredentials: true });
      toast.success("Dipendente ripristinato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore ripristino"); }
  };

  // B71 BIS — Hard-delete dipendente da tab Cestino
  const hardDeleteEmp = async (emp) => {
    try {
      await axios.delete(`${API}/employees/${emp.id}?permanent=true`, { withCredentials: true });
      toast.success(`${emp.first_name} ${emp.last_name} eliminato definitivamente`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione definitiva"); }
  };

  // MIG-25: archive employee with mandatory reason
  const openArchiveDialog = (emp) => {
    setArchiveDlg(emp);
    setArchiveForm({ reason: "dimissioni", last_working_day: "", notes: "" });
  };
  const submitArchive = async () => {
    if (!archiveDlg) return;
    if (!archiveForm.reason) { toast.error("Motivo obbligatorio"); return; }
    setArchiveBusy(true);
    try {
      await axios.post(`${API}/employees/${archiveDlg.id}/archive`, archiveForm, { withCredentials: true });
      toast.success(`${archiveDlg.first_name} ${archiveDlg.last_name} archiviato`);
      setArchiveDlg(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore archiviazione"); }
    finally { setArchiveBusy(false); }
  };
  const unarchiveEmp = async (eid) => {
    try {
      await axios.post(`${API}/employees/${eid}/unarchive`, {}, { withCredentials: true });
      toast.success("Dipendente riattivato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // C19: row click default handler
  const setRowActionPref = (a) => {
    setRowAction(a);
    try { localStorage.setItem("selly_employee_row_default_action", a); } catch {}
  };
  const handleRowClick = (emp) => {
    if (rowAction === "modal") {
      setViewEmpId(emp.id);
    } else {
      // default = card → naviga a Schede con expand
      setActiveTab("schede");
      setExpandedScheda(emp.id);
    }
  };
  const handleRowDblClick = (emp) => {
    // dbl-click sempre apre modale
    setViewEmpId(emp.id);
  };

  const openEditFromDetail = (emp) => {
    setForm({
      first_name: emp.first_name || "", last_name: emp.last_name || "",
      email: emp.email || "", phone: emp.phone || "", job_title: emp.job_title || "",
      department: emp.department || "", classification: emp.classification || "",
      ral: emp.ral || 0, years_experience: emp.years_experience || 0,
      is_manager: !!emp.is_manager, managed_count: emp.managed_count || 0,
      sector: emp.sector || "", manager_id: emp.manager_id || "",
      hr_notes: emp.hr_notes || "", mansionario_id: emp.mansionario_id || "",
    });
    setEditId(emp.id);
    setViewEmpId(null);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editId) await axios.put(`${API}/employees/${editId}`, form, { withCredentials: true });
      else await axios.post(`${API}/employees`, form, { withCredentials: true });
      toast.success(editId ? "Dipendente aggiornato" : "Dipendente aggiunto");
      setOpen(false); resetForm(); load();
    } catch { toast.error("Errore"); }
  };

  const sendTest = async (eid) => {
    try {
      const { data } = await axios.post(`${API}/employees/${eid}/send-test`, {}, { withCredentials: true });
      if (data.email_sent) {
        toast.success(`Email inviata a ${data.email}`);
      } else {
        // Fallback: nessuna email registrata o invio fallito → copia link
        const link = data.link_url || `${window.location.origin}/analisi/dipendente/${data.test_link}`;
        try { await navigator.clipboard.writeText(link); } catch {}
        toast.warning(`Email non inviata${data.email ? '' : ' (dipendente senza email)'}. Link copiato: usa "Copia link" per condividerlo manualmente.`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nell'invio"); }
  };

  const copyAnalysisLink = async (eid, testLink) => {
    const link = `${window.location.origin}/analisi/dipendente/${testLink}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link analisi copiato negli appunti");
    } catch { toast.error("Impossibile copiare il link"); }
  };

  const generateInvite = async () => {
    try {
      const { data } = await axios.post(`${API}/employees/generate-invite`, inviteForm, { withCredentials: true });
      try { await navigator.clipboard.writeText(data.link); } catch { /* clipboard API may be blocked */ }
      // BUG-40: feedback specifico se email fallisce
      const baseMsg = `Invito inviato a ${inviteForm.email || "(no email)"} — scade tra 15 giorni`;
      if (inviteForm.send_email && !data.email_sent) {
        toast.error(`Email non inviata: ${data.email_error || "errore sconosciuto"}. Link copiato negli appunti.`);
      } else {
        toast.success(data.email_sent ? `${baseMsg} (email inviata)` : baseMsg);
      }
      setInviteOpen(false);
      setInviteLink("");
      setInviteForm({ first_name: "", last_name: "", email: "", department: "", is_manager: false, send_email: false });
      load();
    } catch { toast.error("Errore generazione invito"); }
  };

  // FIX-4 BUG-37: reinvia link invito (rigenera invite_code + +15gg scadenza)
  const resendInvite = async (eid) => {
    try {
      const { data } = await axios.post(`${API}/employees/${eid}/resend-invite`, {}, { withCredentials: true });
      try { await navigator.clipboard.writeText(data.link); } catch { /* noop */ }
      toast.success("Link reinviato, scade tra 15 giorni");
      load();
    } catch { toast.error("Errore reinvio link"); }
  };

  // MINI-FIX 51 BIS: invia via email il link ESISTENTE (no rigenerazione code)
  const sendInviteEmail = async (eid) => {
    try {
      const { data } = await axios.post(`${API}/employees/${eid}/send-invite-email`, {}, { withCredentials: true });
      toast.success(`Link inviato a ${data.email}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 400 && detail.toLowerCase().includes("scaduto")) {
        toast.warning("Link scaduto, usa Reinvia link prima");
      } else if (err.response?.status === 400 && detail.toLowerCase().includes("email")) {
        toast.warning(detail);
      } else {
        toast.error("Errore invio email, riprova");
      }
    }
  };

  // FIX-4 BUG-37: annulla invito (hard-delete placeholder pending_registration)
  const cancelInvite = async () => {
    if (!confirmCancelEid) return;
    try {
      await axios.delete(`${API}/employees/${confirmCancelEid}/cancel-invite`, { withCredentials: true });
      toast.success("Invito annullato");
      setConfirmCancelEid(null);
      load();
    } catch { toast.error("Errore annullamento invito"); }
  };

  // FIX-4 BUG-37 / MINI-FIX UX: stato ISCRIZIONE (registrazione), distinto dalla colonna ANALISI.
  // Mappiamo registration_status a 3 stati visivi:
  //   pending valido → giallo "Invito inviato"
  //   pending scaduto → rosso "Invito scaduto"
  //   registered/analysis_sent/analysis_completed → blu "Registrato"
  const getRegStatusInfo = (e) => {
    const isPending = e.registration_status === "pending_registration";
    const isExpired = isPending && e.invite_expires_at && new Date(e.invite_expires_at) < new Date();
    if (isExpired) return { key: "expired", label: "Invito scaduto", tooltip: "Reinvia o annulla", className: "bg-red-50 text-red-700 border-red-200" };
    if (isPending) {
      const exp = e.invite_expires_at ? new Date(e.invite_expires_at) : null;
      const days = exp ? Math.max(0, Math.ceil((exp - new Date()) / 86400000)) : 15;
      // BUG-41: tooltip badge — Email inviata / Link copiato manualmente
      let tooltip = `Scade tra ${days} giorni`;
      if (e.invite_email_sent && e.invite_email_sent_at) {
        try {
          const fmt = new Date(e.invite_email_sent_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
          tooltip = `Email inviata il ${fmt} · Scade tra ${days} giorni`;
        } catch { /* noop */ }
      } else {
        tooltip = `Link generato — email non inviata, copialo manualmente · Scade tra ${days} giorni`;
      }
      return { key: "pending_registration", label: "Invito inviato", tooltip, className: "bg-amber-50 text-amber-700 border-amber-200" };
    }
    // registered / analysis_sent / analysis_completed → tutti "Registrato" lato iscrizione
    return { key: "registered", label: "Registrato", tooltip: e.registered_at ? `Profilo compilato il ${new Date(e.registered_at).toLocaleDateString('it-IT')}` : "", className: "bg-blue-50 text-blue-700 border-blue-200" };
  };

  const filtered = (() => {
    let list = employees;
    if (deptFilter && deptFilter !== "all") list = list.filter(e => e.department === deptFilter);
    if (mgrFilter && mgrFilter !== "all") list = list.filter(e => e.manager_id === mgrFilter);
    if (regStatusFilter && regStatusFilter !== "all") {
      list = list.filter(e => {
        const info = getRegStatusInfo(e);
        // MINI-FIX UX: "pending_registration" filter matcha sia validi (pending) sia scaduti (expired)
        if (regStatusFilter === "pending_registration") return info.key === "pending_registration" || info.key === "expired";
        return info.key === regStatusFilter;
      });
    }
    if (planFilter && planFilter !== "all") {
      list = list.filter(e => (e.plan_aggregate_status || "nessun_piano") === planFilter);
    }
    // FEATURE-04: filtro certificazioni
    if (certFilter === "expiring") {
      list = list.filter(e => (certSummary[e.id]?.in_scadenza || 0) > 0);
    } else if (certFilter === "expired") {
      list = list.filter(e => (certSummary[e.id]?.scaduta || 0) > 0);
    }
    // BUG-65 + B64: default sort alfabetico cognome; se planSort attivo → priority workflow
    if (planSort === "priority_asc" || planSort === "priority_desc") {
      const PRIO = { nessun_piano: 1, da_approvare: 2, da_fare: 3, in_corso: 4, completato: 5 };
      const dir = planSort === "priority_asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        const pa = PRIO[a.plan_aggregate_status || "nessun_piano"] || 99;
        const pb = PRIO[b.plan_aggregate_status || "nessun_piano"] || 99;
        if (pa !== pb) return (pa - pb) * dir;
        // tie-break: alphabetical by surname
        const cognA = (a.last_name || "").toLowerCase();
        const cognB = (b.last_name || "").toLowerCase();
        return cognA.localeCompare(cognB, "it");
      });
    } else {
      list = [...list].sort((a, b) => {
        const cognA = (a.last_name || "").toLowerCase();
        const cognB = (b.last_name || "").toLowerCase();
        if (cognA !== cognB) return cognA.localeCompare(cognB, "it");
        return (a.first_name || "").toLowerCase().localeCompare((b.first_name || "").toLowerCase(), "it");
      });
    }
    return list;
  })();
  const testStatus = { "Non inviato": "bg-gray-100 text-gray-500", "Test inviato": "bg-blue-50 text-blue-700", "In attesa": "bg-yellow-50 text-yellow-700", "In corso": "bg-blue-50 text-blue-700", "Completato": "bg-green-50 text-green-700" };
  const testStatusLabel = { "Test inviato": "Analisi inviata", "Non inviato": "Non inviata", "In attesa": "In attesa", "In corso": "In corso", "Completato": "Completata" };

  // B64: badge stato aggregato Piano Sviluppo (5 stati canonici, server-side rollup)
  const PLAN_AGG_META = {
    nessun_piano: { label: "Nessun piano",  className: "bg-slate-100 text-slate-600 border-slate-200",         priority: 1 },
    da_approvare: { label: "Da approvare",  className: "bg-yellow-100 text-yellow-700 border-yellow-300",       priority: 2 },
    da_fare:      { label: "Da fare",       className: "bg-sky-100 text-sky-700 border-sky-200",                priority: 3 },
    in_corso:     { label: "In corso",      className: "bg-blue-100 text-blue-700 border-blue-300",             priority: 4 },
    completato:   { label: "Completato",    className: "bg-emerald-100 text-emerald-700 border-emerald-300",   priority: 5 },
  };
  const getPlanInfo = (e) => {
    const status = e.plan_aggregate_status || "nessun_piano";
    const meta = PLAN_AGG_META[status] || PLAN_AGG_META.nessun_piano;
    const bd = e.plan_status_breakdown || {};
    const tooltip = `${e.plan_task_count || 0} task totali · ${bd.da_approvare || 0} da approvare · ${bd.da_fare || 0} da fare · ${bd.in_corso || 0} in corso · ${bd.completato || 0} completati`;
    return { label: meta.label, className: meta.className, tooltip, priority: meta.priority };
  };

  const openSchedaForEmp = (eid) => {
    setExpandedScheda(eid);
    setActiveTab("schede");
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="scheda-${eid}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };
  return (
    <div className="animate-fade-in" data-testid="employees-page">
      <PageHeader title="Dipendenti" subtitle={`${employees.length} dipendenti`}>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="w-[200px] h-10"><SelectValue placeholder="Tutti i reparti" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti i reparti</SelectItem>{DEPARTMENTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={mgrFilter} onValueChange={setMgrFilter}>
            <SelectTrigger className="w-[200px] h-10"><SelectValue placeholder="Tutti i manager" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti i manager</SelectItem>{employees.filter(e=>e.is_manager && e.registration_status !== "pending_registration").map(m=><SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={certFilter} onValueChange={setCertFilter}>
            <SelectTrigger className="w-[210px] h-10" data-testid="cert-filter-trigger"><SelectValue placeholder="Certificazioni" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le certificazioni</SelectItem>
              <SelectItem value="expiring">In scadenza (≤30gg)</SelectItem>
              <SelectItem value="expired">Scadute</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" data-testid="invite-employee-btn" className="border-[#56A3F5]/30 text-[#1B4FD8]" onClick={()=>setInviteOpen(true)}><LinkSimple size={18} className="mr-2" />Link Invito</Button>
          <Button data-testid="add-employee-btn" className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" onClick={()=>setOpen(true)}><Plus size={18} className="mr-2"/>Aggiungi</Button>
      </PageHeader>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={o => { setInviteOpen(o); if(!o) { setInviteLink(""); setInviteForm({first_name:"",last_name:"",email:"",department:"",is_manager:false,send_email:false}); }}}>
        <DialogContent>
          <DialogHeader><DialogTitle>Genera Link Auto-Registrazione</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <p className="text-xs text-[#0D1F3C]/50">Il dipendente ricevera un link per compilare il proprio profilo. Il link scade dopo 15 giorni.</p>
            {/* BUG-39: Nome + Cognome opzionali per personalizzare email + precompilare form auto-reg */}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs mb-1 block">Nome (opzionale)</Label><Input value={inviteForm.first_name} onChange={e=>setInviteForm(p=>({...p,first_name:e.target.value}))} placeholder="Mario" data-testid="invite-firstname" /></div>
              <div><Label className="text-xs mb-1 block">Cognome (opzionale)</Label><Input value={inviteForm.last_name} onChange={e=>setInviteForm(p=>({...p,last_name:e.target.value}))} placeholder="Rossi" data-testid="invite-lastname" /></div>
            </div>
            <div><Label className="text-xs mb-1 block">Email dipendente (opzionale)</Label><Input value={inviteForm.email} onChange={e=>setInviteForm(p=>({...p,email:e.target.value}))} placeholder="Se inserita, inviamo l'email automaticamente" /></div>
            <div><Label className="text-xs mb-1 block">Reparto</Label>
              <Select value={inviteForm.department} onValueChange={v=>setInviteForm(p=>({...p,department:v}))}><SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent position="popper" sideOffset={4}>{DEPARTMENTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
            <div className="flex items-center gap-2"><Switch checked={inviteForm.is_manager} onCheckedChange={v=>setInviteForm(p=>({...p,is_manager:v}))} /><Label className="text-xs">Responsabile</Label></div>
            <div className="flex items-center gap-2" data-testid="toggle-send-email">
              <Switch checked={inviteForm.send_email} onCheckedChange={v=>setInviteForm(p=>({...p,send_email:v}))} disabled={!inviteForm.email} />
              <Label className={`text-xs ${!inviteForm.email ? "opacity-40" : ""}`}>Invia anche per email {!inviteForm.email && <span className="text-[10px]">(richiede email)</span>}</Label>
            </div>
            <Button onClick={generateInvite} data-testid="gen-invite-btn" className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C]">Genera Link</Button>
            {inviteLink && <div className="p-3 bg-[#10B981]/5 border border-[#10B981]/30 rounded-lg"><p className="text-xs text-[#0D1F3C]/50 mb-1">Link generato e copiato:</p><p className="text-xs text-[#0D1F3C] break-all font-mono">{inviteLink}</p></div>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={open} onOpenChange={o=>{setOpen(o);if(!o)resetForm();}}>
        <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{editId?"Modifica":"Aggiungi"} Dipendente</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs mb-1 block">Nome *</Label><Input data-testid="emp-first-name" value={form.first_name} onChange={e=>up("first_name",e.target.value)} /></div>
                  <div><Label className="text-xs mb-1 block">Cognome *</Label><Input data-testid="emp-last-name" value={form.last_name} onChange={e=>up("last_name",e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs mb-1 block">Email Aziendale</Label><Input data-testid="emp-email" type="email" value={form.email} onChange={e=>up("email",e.target.value)} /></div>
                  <div><Label className="text-xs mb-1 block">Telefono</Label><Input data-testid="emp-phone" type="tel" value={form.phone} onChange={e=>up("phone",e.target.value)} placeholder="es. +39 333 1234567" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs mb-1 block">Job Title</Label><Input value={form.job_title} onChange={e=>up("job_title",e.target.value)} /></div>
                  <div><Label className="text-xs mb-1 block">Reparto</Label>
                    <Select value={form.department} onValueChange={v=>up("department",v)}><SelectTrigger data-testid="emp-department"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{DEPARTMENTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs mb-1 block">Inquadramento</Label><Input value={form.classification} onChange={e=>up("classification",e.target.value)} /></div>
                  <div><Label className="text-xs mb-1 block">Anni Anzianita</Label><Input type="number" value={form.years_experience} onChange={e=>up("years_experience",parseInt(e.target.value)||0)} /></div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2"><Switch checked={form.is_manager} onCheckedChange={v=>up("is_manager",v)} /><Label className="text-xs">Responsabile</Label></div>
                  {form.is_manager && <div><Label className="text-xs mb-1 block">Persone gestite</Label><Input type="number" className="w-24" value={form.managed_count} onChange={e=>up("managed_count",parseInt(e.target.value)||0)} /></div>}
                </div>
                <div><Label className="text-xs mb-1 block">Responsabile</Label>
                  <Select value={form.manager_id || ""} onValueChange={v=>up("manager_id",v)}>
                    <SelectTrigger data-testid="emp-manager"><SelectValue placeholder="Nessun responsabile" /></SelectTrigger>
                    <SelectContent>{employees.filter(e=>e.id!==editId && e.registration_status !== "pending_registration").map(e=><SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.job_title||e.department}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs mb-1 block">Mansionario di riferimento <span className="text-[#0D1F3C]/40">(opzionale)</span></Label>
                  <Select value={form.mansionario_id || ""} onValueChange={v=>up("mansionario_id", v === "__none__" ? "" : v)}>
                    <SelectTrigger data-testid="emp-mansionario"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nessuno</SelectItem>
                      {mansionari
                        .filter(m => !form.department || !m.macro_area || m.macro_area === form.department || true)
                        .map(m => <SelectItem key={m.id} value={m.id}>{m.title} — {m.macro_area}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Note HR <span className="text-[#0D1F3C]/40">(opzionale)</span></Label>
                  <Textarea rows={3} value={form.hr_notes} onChange={e => up("hr_notes", e.target.value)}
                    placeholder="Es: preferenze orarie, osservazioni 1:1, aspettative carriera..." data-testid="emp-hr-notes" />
                  <p className="text-[10px] text-[#0D1F3C]/50 mt-1">Le note operative non influenzano il report. Solo info rilevanti (aspettative, motivazioni) sono considerate.</p>
                </div>
                <Button onClick={save} data-testid="emp-save-btn" className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C]">{editId?"Aggiorna":"Aggiungi"}</Button>
              </div>
            </DialogContent>
          </Dialog>

      <EmployeeDetailDialog empId={viewEmpId} open={!!viewEmpId} onClose={() => setViewEmpId(null)} onRefresh={load} onEdit={openEditFromDetail} />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-[#0D1F3C]/8 p-1 rounded-lg mb-4">
          <TabsTrigger value="lista" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-6">Lista</TabsTrigger>
          <TabsTrigger value="schede" data-testid="tab-schede" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-6">
            <Files size={16} className="mr-2" />Schede
          </TabsTrigger>
          <TabsTrigger value="organigramma" data-testid="tab-organigramma" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-6">
            <TreeStructure size={16} className="mr-2" />Organigramma
          </TabsTrigger>
          <TabsTrigger value="certificazioni" data-testid="tab-certificazioni" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-6">
            <Certificate size={16} className="mr-2" />Certificazioni
          </TabsTrigger>
        </TabsList>
        <TabsContent value="lista">
          {/* BUG-27: tabs Attivi/Cestino */}
          <div className="flex items-center gap-1 mb-4 border-b border-[#0D1F3C]/8" data-testid="emp-status-tabs">
            <button onClick={() => setStatusTab("attivi")} data-testid="tab-attivi"
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${statusTab === "attivi" ? "border-[#1B4FD8] text-[#1B4FD8]" : "border-transparent text-[#0D1F3C]/60 hover:text-[#0D1F3C]"}`}>
              Attivi
              <span className={`ml-2 text-[11px] px-1.5 py-0.5 rounded ${statusTab === "attivi" ? "bg-[#1B4FD8]/10" : "bg-[#0D1F3C]/5"}`}>
                {statusTab === "attivi" ? employees.length : "·"}
              </span>
            </button>
            <button onClick={() => setStatusTab("archivio")} data-testid="tab-archivio"
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${statusTab === "archivio" ? "border-[#0D1F3C] text-[#0D1F3C]" : "border-transparent text-[#0D1F3C]/60 hover:text-[#0D1F3C]"}`}>
              <Archive size={13} className="inline mr-1" /> Archivio
              <span className={`ml-2 text-[11px] px-1.5 py-0.5 rounded ${statusTab === "archivio" ? "bg-[#0D1F3C]/10" : "bg-[#0D1F3C]/5"}`}>
                {byDeptData.archived_count || archivedEmps.length}
              </span>
            </button>
            <button onClick={() => setStatusTab("cestino")} data-testid="tab-cestino"
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${statusTab === "cestino" ? "border-red-500 text-red-600" : "border-transparent text-[#0D1F3C]/60 hover:text-[#0D1F3C]"}`}>
              Cestino
              <span className={`ml-2 text-[11px] px-1.5 py-0.5 rounded ${statusTab === "cestino" ? "bg-red-500/10" : "bg-[#0D1F3C]/5"}`}>
                {trashCount}
              </span>
            </button>
            {/* FIX-4 BUG-37: filtro stato registrazione */}
            {statusTab === "attivi" && (
              <div className="ml-auto pb-2">
                <Select value={regStatusFilter} onValueChange={setRegStatusFilter}>
                  <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="reg-status-filter">
                    <SelectValue placeholder="Filtra per iscrizione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="pending_registration">Invito inviato</SelectItem>
                    <SelectItem value="registered">Registrato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Card className="border-[#0D1F3C]/8">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Nome</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Ruolo</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Reparto</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Responsabile</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Tipo</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Iscrizione</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Analisi</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50" data-testid="col-plan-header">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPlanSort(s => s === "priority_asc" ? "priority_desc" : s === "priority_desc" ? null : "priority_asc")}
                      className="hover:text-[#1B4FD8] transition-colors flex items-center gap-1"
                      title={planSort === "priority_asc" ? "Priorità ↑ (click per ↓)" : planSort === "priority_desc" ? "Priorità ↓ (click per default)" : "Click per ordinare per priorità workflow"}
                      data-testid="plan-sort-toggle"
                    >
                      Piano Sviluppo
                      {planSort === "priority_asc" && <span className="text-[#1B4FD8]">↑</span>}
                      {planSort === "priority_desc" && <span className="text-[#1B4FD8]">↓</span>}
                    </button>
                    <Select value={planFilter} onValueChange={setPlanFilter}>
                      <SelectTrigger className="h-6 w-[24px] text-[10px] border-none p-0 bg-transparent" data-testid="plan-filter">
                        <SelectValue><span className="text-[10px]">{planFilter === "all" ? "▾" : "●"}</span></SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti</SelectItem>
                        <SelectItem value="nessun_piano">Nessun piano</SelectItem>
                        <SelectItem value="da_approvare">Da approvare</SelectItem>
                        <SelectItem value="da_fare">Da fare</SelectItem>
                        <SelectItem value="in_corso">In corso</SelectItem>
                        <SelectItem value="completato">Completato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 text-right">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.length===0?<TableRow><TableCell colSpan={9} className="text-center py-8 text-[#0D1F3C]/40 text-sm">Nessun dipendente</TableCell></TableRow>:
                  filtered.map(e=>{
                    const manager = employees.find(m => m.id === e.manager_id);
                    const regInfo = getRegStatusInfo(e);
                    const isPending = e.registration_status === "pending_registration";
                    return (
                    <TableRow key={e.id} data-testid={`emp-row-${e.id}`}>
                      <TableCell>
                        {isPending ? (
                          // BUG-45: nome principale + email piccola sotto
                          <div className="flex flex-col" data-testid={`emp-pending-name-${e.id}`}>
                            {(e.first_name || e.last_name) ? (
                              <span className="text-sm font-medium text-[#0D1F3C]">{`${e.first_name || ""} ${e.last_name || ""}`.trim()}</span>
                            ) : (
                              <span className="text-sm italic text-[#0D1F3C]/50">Invitato — in attesa di registrazione</span>
                            )}
                            {e.email && <span className="text-xs text-[#6B7280] mt-0.5">{e.email}</span>}
                          </div>
                        ) : (
                          <button
                            onClick={()=>handleRowClick(e)}
                            onDoubleClick={()=>handleRowDblClick(e)}
                            className="font-medium text-[#0D1F3C] hover:text-[#1B4FD8] transition-colors cursor-pointer"
                            title={rowAction === "card" ? "Click: apre scheda · doppio-click: modale" : "Click: modale · doppio-click: modale"}
                          >{e.first_name} {e.last_name}</button>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-[#0D1F3C]/60">{e.job_title || (isPending ? "—" : "-")}</TableCell>
                      <TableCell className="text-sm text-[#0D1F3C]/60">{e.department||'-'}</TableCell>
                      <TableCell className="text-sm text-[#0D1F3C]/60">{manager ? `${manager.first_name} ${manager.last_name}` : '—'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{e.is_manager?'Responsabile':'Dipendente'}</Badge></TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-[11px] ${regInfo.className}`}
                          title={regInfo.tooltip || ""}
                          data-testid={`emp-reg-status-${e.id}`}
                        >
                          {regInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {/* BUG-26: 3 stati bustina coerenti con CandidatesPage */}
                        {isPending ? (
                          <span className="text-[#0D1F3C]/30 text-xs">—</span>
                        ) : (() => {
                          const fmt = (iso) => { try { return new Date(iso).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }); } catch { return ''; } };
                          if (e.test_status === "Completato") {
                            return (
                              <div className="flex items-center gap-1.5" title={e.test_completed_at ? `Completata il ${fmt(e.test_completed_at)}` : 'Completata'} data-testid={`emp-status-icon-${e.id}`}>
                                <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                                <Badge className={`text-xs ${testStatus[e.test_status]||''}`}>{testStatusLabel[e.test_status] || e.test_status}</Badge>
                              </div>
                            );
                          }
                          if (e.analysis_link_sent_at) {
                            return (
                              <div className="flex items-center gap-1.5" title={`Inviata il ${fmt(e.analysis_link_sent_at)} — in attesa di completamento`} data-testid={`emp-status-icon-${e.id}`}>
                                <EnvelopeOpen size={18} weight="duotone" className="text-emerald-600" />
                                <ArrowsClockwise size={12} className="text-emerald-600" />
                                <Badge className={`text-xs ${testStatus[e.test_status]||''}`}>{testStatusLabel[e.test_status] || e.test_status}</Badge>
                              </div>
                            );
                          }
                          return (
                            <div className="flex items-center gap-1.5" title="Analisi non ancora inviata" data-testid={`emp-status-icon-${e.id}`}>
                              <EnvelopeSimple size={18} weight="duotone" className="text-[#1B4FD8]" />
                              <Badge className={`text-xs ${testStatus[e.test_status]||''}`}>{testStatusLabel[e.test_status] || e.test_status}</Badge>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {/* FEATURE-01: badge Piano Sviluppo */}
                        {isPending ? (
                          <span className="text-[#0D1F3C]/30 text-xs">—</span>
                        ) : (() => {
                          const info = getPlanInfo(e);
                          return (
                            <button
                              onClick={() => openSchedaForEmp(e.id)}
                              className="cursor-pointer"
                              title="Apri scheda con roadmap"
                              data-testid={`emp-plan-badge-${e.id}`}
                            >
                              <Badge variant="outline" className={`text-[11px] ${info.className}`} title={info.tooltip}>
                                {info.label}
                              </Badge>
                            </button>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {statusTab === "cestino" ? (
                            <>
                              <Button variant="ghost" size="sm" onClick={()=>setViewEmpId(e.id)} title="Visualizza scheda"><Eye size={16}/></Button>
                              <Button size="sm" onClick={()=>restoreEmp(e.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1"
                                data-testid={`emp-restore-${e.id}`}>
                                <ArrowsClockwise size={14} /> Ripristina
                              </Button>
                              <ConfirmDelete
                                title="Eliminare definitivamente?"
                                description={`Stai per eliminare DEFINITIVAMENTE ${e.first_name} ${e.last_name} dal database. Verranno cancellati anche tutti i dati collegati (analisi, report, certificazioni, piani di sviluppo). Questa azione NON puo essere annullata.`}
                                onConfirm={() => hardDeleteEmp(e)}
                              >
                                <Button variant="ghost" size="sm" title="Elimina definitivamente" data-testid={`emp-hard-delete-${e.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs gap-1">
                                  <Trash size={14} /> Definitivo
                                </Button>
                              </ConfirmDelete>
                            </>
                          ) : statusTab === "archivio" ? (
                            <>
                              <Button variant="ghost" size="sm" onClick={()=>setViewEmpId(e.id)} title="Visualizza scheda"><Eye size={16}/></Button>
                              <Button size="sm" onClick={()=>unarchiveEmp(e.id)} className="bg-[#1B4FD8] hover:bg-[#0D1F3C] text-white text-xs gap-1"
                                data-testid={`emp-unarchive-${e.id}`}>
                                <ArrowCounterClockwise size={14} /> Riattiva
                              </Button>
                            </>
                          ) : isPending ? (
                            // FIX-4 BUG-37: azioni limitate per pending_registration
                            // BUG-41: icona ✉️ visibile solo se email NON ancora inviata
                            <>
                              {!e.invite_email_sent && (() => {
                                const isExpired = regInfo.key === "expired";
                                const hasEmail = !!e.email;
                                const disabled = isExpired || !hasEmail;
                                const tooltip = isExpired
                                  ? "Link scaduto, reinvialo prima"
                                  : !hasEmail
                                    ? "Email mancante: usa Reinvia link"
                                    : `Invia link via email a ${e.email}`;
                                return (
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={() => sendInviteEmail(e.id)}
                                    disabled={disabled}
                                    title={tooltip}
                                    data-testid={`send-invite-email-${e.id}`}
                                    className={disabled ? "opacity-40 cursor-not-allowed" : ""}
                                  >
                                    <EnvelopeSimple size={16} className="text-[#10B981]" />
                                  </Button>
                                );
                              })()}
                              <Button variant="ghost" size="sm" onClick={() => resendInvite(e.id)} title="Reinvia link invito (rigenera codice + reset scadenza 15gg)" data-testid={`resend-invite-${e.id}`}>
                                <ArrowClockwise size={16} className="text-[#1B4FD8]" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setConfirmCancelEid(e.id)} title="Annulla invito" data-testid={`cancel-invite-${e.id}`}>
                                <XIcon size={16} className="text-red-600" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button variant="ghost" size="sm" onClick={()=>setViewEmpId(e.id)} title="Visualizza scheda"><Eye size={16}/></Button>
                              <Button variant="ghost" size="sm" onClick={()=>{setForm(e);setEditId(e.id);setOpen(true);}} title="Modifica"><PencilSimple size={16}/></Button>
                              {e.test_status!=="Completato" && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={()=>sendTest(e.id)} data-testid={`send-test-${e.id}`} title={e.email ? `Invia analisi via email a ${e.email}` : "Invia analisi (genera link)"}>
                                    <PaperPlaneTilt size={16} className="text-[#1B4FD8]"/>
                                  </Button>
                                  {e.test_link && (
                                    <Button variant="ghost" size="sm" onClick={()=>copyAnalysisLink(e.id, e.test_link)} data-testid={`copy-link-${e.id}`} title="Copia link analisi (backup)">
                                      <LinkSimple size={16} className="text-[#0D1F3C]/40"/>
                                    </Button>
                                  )}
                                </>
                              )}
                              {/* MIG-25: archivia dipendente (cessazione) */}
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => openArchiveDialog(e)}
                                title="Archivia dipendente (cessazione/dimissioni)"
                                data-testid={`archive-emp-${e.id}`}
                                className="text-[#0D1F3C]/60 hover:text-[#0D1F3C]"
                              >
                                <Archive size={16} />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );})}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        <TabsContent value="schede">
          {/* FEATURE-02: card accordion con roadmap di sviluppo */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Input placeholder="Cerca per nome..." className="w-64 h-9"
              onChange={e => {
                const q = e.target.value.toLowerCase().trim();
                if (!q) return;
                const found = employees.find(emp => `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(q));
                if (found) setExpandedScheda(found.id);
              }} data-testid="schede-search" />
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="w-[180px] h-9 text-xs"><SelectValue placeholder="Reparto" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti i reparti</SelectItem>{DEPARTMENTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-3">
            {filtered.filter(e => e.registration_status !== "pending_registration").length === 0 ? (
              <Card className="border-[#0D1F3C]/8"><CardContent className="py-12 text-center text-[#0D1F3C]/40 text-sm">Nessun dipendente registrato</CardContent></Card>
            ) : filtered
                .filter(e => e.registration_status !== "pending_registration")
                .map(e => {
                  const manager = employees.find(m => m.id === e.manager_id);
                  return (
                    <SchedaCard
                      key={e.id}
                      emp={{ ...e, manager_name: manager ? `${manager.first_name} ${manager.last_name}` : "—" }}
                      expanded={expandedScheda === e.id}
                      onToggle={(v) => {
                        if (typeof v === "string" && v.startsWith("open-report:")) {
                          setViewEmpId(v.split(":")[1]);
                        } else {
                          setExpandedScheda(v);
                        }
                      }}
                      onChanged={() => load()}
                    />
                  );
                })}
          </div>
        </TabsContent>
        <TabsContent value="organigramma">
          {/* FIX-4 BUG-37: pending_registration esclusi dall'organigramma */}
          <OrgChart employees={employees.filter(e => e.registration_status !== "pending_registration")} />
        </TabsContent>
        <TabsContent value="certificazioni">
          {/* Batch 62.B: certificazioni aggregate workspace-level */}
          <EmployeeCertificationsTab employees={employees} />
        </TabsContent>
      </Tabs>

      {/* FIX-4 BUG-37: dialog conferma annullamento invito */}
      <Dialog open={!!confirmCancelEid} onOpenChange={(o) => { if (!o) setConfirmCancelEid(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Annullare l'invito?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#0D1F3C]/70">Il link di invito non funzionerà più e il record verrà eliminato definitivamente. Questa operazione non può essere annullata.</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmCancelEid(null)} data-testid="cancel-invite-no">Annulla</Button>
            <Button onClick={cancelInvite} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-invite-yes">Sì, annulla invito</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* MIG-25: Archive employee dialog */}
      <Dialog open={!!archiveDlg} onOpenChange={(v) => !v && setArchiveDlg(null)}>
        <DialogContent className="max-w-md" data-testid="archive-emp-dialog">
          <DialogHeader>
            <DialogTitle>Archivia dipendente</DialogTitle>
          </DialogHeader>
          {archiveDlg && (
            <div className="space-y-3 mt-2">
              <p className="text-sm text-[#0D1F3C]/70">
                <strong>{archiveDlg.first_name} {archiveDlg.last_name}</strong> · {archiveDlg.job_title || "—"}
              </p>
              <div>
                <Label className="text-xs mb-1 block">Motivo cessazione *</Label>
                <Select value={archiveForm.reason} onValueChange={v => setArchiveForm(p => ({ ...p, reason: v }))}>
                  <SelectTrigger data-testid="emp-archive-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dimissioni">Dimissioni</SelectItem>
                    <SelectItem value="licenziamento">Licenziamento</SelectItem>
                    <SelectItem value="pensione">Pensione</SelectItem>
                    <SelectItem value="fine_contratto">Fine contratto</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Data ultima giornata</Label>
                <Input type="date" value={archiveForm.last_working_day} onChange={e => setArchiveForm(p => ({ ...p, last_working_day: e.target.value }))} data-testid="emp-archive-last-day" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Note <span className="text-[#0D1F3C]/40">(opzionale)</span></Label>
                <Textarea rows={2} value={archiveForm.notes} onChange={e => setArchiveForm(p => ({ ...p, notes: e.target.value }))} placeholder="Es. accordo di transazione · trasferito altra azienda" data-testid="emp-archive-notes" />
              </div>
              <p className="text-[11px] text-[#0D1F3C]/50">Lo storico anagrafico, report e piano sviluppo restano consultabili. Il dipendente è escluso da elenchi attivi e analisi.</p>
            </div>
          )}
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setArchiveDlg(null)}>Annulla</Button>
            <Button onClick={submitArchive} disabled={archiveBusy} className="bg-[#0D1F3C] hover:bg-[#0D1F3C]/80" data-testid="emp-archive-confirm-btn">
              {archiveBusy ? "Archiviazione…" : "Archivia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
